// ReviewPitchesTab.jsx — LP "Review Pitches" tab panel, extracted from
// LimitedPartnerPortal. JSX-only extraction: the review submit-phase machine
// (idle/saving/success + timers), form population effects, scroll restore,
// filters, and the pitch list memo all stay lifted in the portal because
// parent effects and the badge/stats pipeline drive them; they flow in as
// props under their original names.
import React, { useState } from "react";
import { EmptyState } from "../../ui/retro";
import { isLMIZip, LMI_THRESHOLD, LMI_ACS_YEAR } from "../../../helpers/lmiZips";
import { PITCH_CATEGORIES } from "../../../data/pitchCategories";
import { getCurrentQuarter } from "../../../services/statsTracking";
import StatusIcon from "../../icons/StatusIcon";
import OSIcon32, { CATEGORY_ICONS } from "../../icons/OSIcon32";
import { externalVideoHost, safeVideoUrl } from "../../../utils/safeVideoUrl";

// Seven-column grid shared by the list header and every row.
const LIST_GRID = "1.7fr 1fr 0.6fr 0.5fr 0.8fr 72px 120px";

// Status → chip fill + review-mark icon. Sentence-case word next to the
// mark, so colour is never the only signal.
const chipForRating = (rating) => {
  switch (rating) {
    case "Favorite":
      return { label: "Favorite", icon: "favorite", bg: "var(--green-mid)", fg: "var(--mb-ink)" };
    case "Consideration":
      return { label: "Consider", icon: "consider", bg: "var(--mb-butter)", fg: "var(--mb-ink)" };
    case "Pass":
      return { label: "Pass", icon: "pass", bg: "var(--mb-paper-deep)", fg: "var(--mb-ink)" };
    case "Ineligible":
      return { label: "Ineligible", icon: "ineligible", bg: "var(--mb-paper-deep)", fg: "var(--mb-ink)" };
    default:
      return { label: "Needs review", icon: "norating", bg: "var(--mb-magenta)", fg: "var(--mb-chalk)" };
  }
};

export default function ReviewPitchesTab({
  user,
  userChapter,
  isSuperAdmin,
  lpPitches,
  lpPitchesLoaded,
  lpFilteredPitches,
  selectedPitch,
  setSelectedPitch,
  reviews,
  reviewFormData,
  pitchComments,
  reviewSubmitPhase,
  reviewSubmitWasEdit,
  hidePassedReviews,
  setHidePassedReviews,
  reviewSearchTerm,
  setReviewSearchTerm,
  reviewFilter,
  setReviewFilter,
  reviewChapterFilter,
  setReviewChapterFilter,
  reviewQuarterFilter,
  setReviewQuarterFilter,
  reviewCategoryFilter,
  setReviewCategoryFilter,
  getNextUnreviewedPitch,
  selectPitchForReview,
  handleBackToList,
  handleReviewSubmit,
  handleReviewFormChange,
  handleLpPitchExport,
  MultiSelectDropdown,
  formatDate,
  awardedDollars = 0,
  reviewedThisQuarter = 0,
  onQuickRate,
  isMobileViewport = false,
}) {
  // Row highlight for the action bar — separate from selectedPitch (which
  // opens the detail view). Click selects; double-click / Enter opens.
  const [selectedRowId, setSelectedRowId] = useState(null);
  const selectedRow = lpFilteredPitches.find((p) => p.id === selectedRowId) || null;
  const selectedRowIndex = lpFilteredPitches.findIndex((p) => p.id === selectedRowId);

  const currentQuarter = getCurrentQuarter();
  const needsReviewCount = lpPitches.filter((p) => p && p.id && !reviews[p.id]).length;
  const favoritesCount = Object.values(reviews).filter((r) => r?.overallLpRating === "Favorite").length;
  const currentYear = new Date().getFullYear();
  // Pitch video links are public form input — only open ones that pass the
  // shared check (see utils/safeVideoUrl), and name the host they lead to.
  const selectedVideoLink =
    safeVideoUrl(selectedPitch?.pitchVideoUrl) || safeVideoUrl(selectedPitch?.pitchVideoFile);
  const selectedVideoHost = externalVideoHost(selectedVideoLink);

  const moveSelection = (delta) => {
    if (lpFilteredPitches.length === 0) return;
    const nextIndex = selectedRowIndex === -1
      ? (delta > 0 ? 0 : lpFilteredPitches.length - 1)
      : Math.min(lpFilteredPitches.length - 1, Math.max(0, selectedRowIndex + delta));
    setSelectedRowId(lpFilteredPitches[nextIndex].id);
  };

  const RATE_ACTIONS = [
    { rating: "Favorite", label: "Favorite", icon: "favorite", bg: "var(--green-mid)" },
    { rating: "Consideration", label: "Consider", icon: "consider", bg: "var(--mb-butter)" },
    { rating: "Pass", label: "Pass", icon: "pass", bg: "var(--mb-paper-deep)" },
    { rating: "Ineligible", label: "Ineligible", icon: "ineligible", bg: "var(--error-bg)" },
  ];
  return (
        <>
          {/* --- Pitch List View --- */}
          {!selectedPitch ? (
            <div className="pr-list" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
              {/* Pane header — quarter context, title, live stat panels */}
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {currentQuarter} · {userChapter || "All chapters"}
                  </div>
                  <h2 className="win95-pane-header__title">Review pitches</h2>
                </div>
                <div className="win95-pane-header__stats">
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Needs review</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                      {needsReviewCount}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Favorites</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                      {favoritesCount}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Awarded {currentYear}</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                      ${awardedDollars >= 1000 ? `${Math.round(awardedDollars / 1000)}K` : awardedDollars}
                    </div>
                  </div>
                </div>
              </div>

              {/* Toolbar — search, filters, export */}
              <div className="win95-toolbar" role="group" aria-label="Filter pitches">
                <input
                  type="search"
                  className="win95-field"
                  style={{ width: 220, flex: "none" }}
                  aria-label="Search pitches"
                  placeholder="Search name / business…"
                  value={reviewSearchTerm}
                  onChange={(e) => setReviewSearchTerm(e.target.value)}
                />
                <div className="win95-toolbar__sep" aria-hidden="true" />
                {isSuperAdmin && (
                  <div className="win95-select">
                    <select
                      className="win95-select__native"
                      aria-label="Filter by chapter"
                      value={reviewChapterFilter}
                      onChange={(e) => setReviewChapterFilter(e.target.value)}
                    >
                      <option value="">All chapters</option>
                      {[...new Set(lpPitches.map((p) => p.chapter).filter(Boolean))].sort().map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                    <span className="win95-select__arrow" aria-hidden="true" />
                  </div>
                )}
                <MultiSelectDropdown
                  options={[...new Set(lpPitches.map((p) => p.quarter).filter(q => q && q !== "Invalid Quarter"))]
                    .sort((a, b) => {
                      const [aQ, aY] = a.split(' ');
                      const [bQ, bY] = b.split(' ');
                      if (bY !== aY) return parseInt(bY) - parseInt(aY);
                      return parseInt(bQ.substring(1)) - parseInt(aQ.substring(1));
                    })}
                  selected={reviewQuarterFilter}
                  onChange={setReviewQuarterFilter}
                  placeholder="All quarters"
                />
                <div className="win95-select">
                  <select
                    className="win95-select__native"
                    aria-label="Filter by category"
                    value={reviewCategoryFilter}
                    onChange={(e) => setReviewCategoryFilter(e.target.value)}
                  >
                    <option value="">All categories</option>
                    {PITCH_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <span className="win95-select__arrow" aria-hidden="true" />
                </div>
                <div className="win95-select">
                  <select
                    className="win95-select__native"
                    aria-label="Filter by review status"
                    value={reviewFilter}
                    onChange={(e) => setReviewFilter(e.target.value)}
                  >
                    <option value="all">All pitches</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="notReviewed">Not reviewed</option>
                    <option value="favorites">Favorites only</option>
                  </select>
                  <span className="win95-select__arrow" aria-hidden="true" />
                </div>
                <div className="win95-toolbar__sep" aria-hidden="true" />
                <label className="win95-check">
                  <input
                    type="checkbox"
                    checked={hidePassedReviews}
                    onChange={() => setHidePassedReviews(!hidePassedReviews)}
                  />
                  Hide passes
                </label>
                <div className="win95-toolbar__spacer" />
                <button
                  type="button"
                  className="win95-btn win95-btn--compact"
                  onClick={handleLpPitchExport}
                  title="Export filtered pitches to CSV (all fields)"
                >
                  <OSIcon32 kind="sheet" size={13} />
                  Export
                </button>
              </div>

              {/* Pitch list or empty state */}
              {lpFilteredPitches.length === 0 ? (
                <div style={{ padding: 20, flex: 1, overflowY: "auto" }} className="win95-scroll">
                  {!lpPitchesLoaded ? (
                    <EmptyState
                      icon={null}
                      title="Loading pitches…"
                      description="One moment while we pull the latest submissions."
                    />
                  ) : lpPitches.length === 0 ? (
                    <EmptyState
                      icon={null}
                      title="No pitches yet this quarter"
                      description={
                        <>
                          When founders in <strong>{userChapter || 'your chapter'}</strong> submit, they'll land here for your review.
                          Share <a href="/pitch" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mb-magenta)' }}>goodneighbor.fund/pitch</a> to spread the word.
                        </>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={null}
                      title="No pitches match"
                      description="Try clearing a filter or widening your search."
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className="win95-listhead" style={{ gridTemplateColumns: LIST_GRID }}>
                    {["Business", "Founder", "Chapter", "Qtr", "Category", "Comments", "Status"].map((h) => (
                      <div key={h} className="win95-listhead__cell">{h}</div>
                    ))}
                  </div>
                  <div className="win95-listbody win95-scroll" role="listbox" aria-label="Pitches">
                    {lpFilteredPitches.map((p) => {
                      if (!p || !p.id) return null;
                      const review = reviews[p.id];
                      const rating = review?.overallLpRating;
                      const chip = p.isWinner
                        ? { label: "Winner", icon: "trophy", bg: "var(--mb-aqua)", fg: "var(--mb-chalk)" }
                        : chipForRating(rating);
                      const isSelected = selectedRowId === p.id;
                      const commentCount = (pitchComments[p.id] || []).length;
                      const fullSummary = p.aiSummary || p.valueProp || "";
                      const summary = fullSummary.length > 240
                        ? `${fullSummary.slice(0, 240).trimEnd()}…`
                        : fullSummary;
                      return (
                        <div
                          key={p.id}
                          role="option"
                          aria-selected={isSelected}
                          tabIndex={0}
                          className={`win95-row ${isSelected ? "is-selected" : ""}`}
                          style={{ gridTemplateColumns: LIST_GRID }}
                          onClick={() => {
                            // Touch has no double-click: a tap selects and
                            // opens in one gesture. Desktop keeps
                            // select-on-click / open-on-double-click.
                            setSelectedRowId(p.id);
                            if (isMobileViewport) selectPitchForReview(p);
                          }}
                          onDoubleClick={() => selectPitchForReview(p)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); selectPitchForReview(p); }
                            if (e.key === "ArrowDown") { e.preventDefault(); moveSelection(1); }
                            if (e.key === "ArrowUp") { e.preventDefault(); moveSelection(-1); }
                          }}
                          title={`Click the business name to open & ${review ? "edit" : "submit"} the review. Submitted ${formatDate(p.createdAt || p.createdDate)}.`}
                        >
                          <div className="win95-row__cell win95-row__cell--primary">
                            <OSIcon32 kind={CATEGORY_ICONS[p.category] || "note"} size={17} />
                            <button
                              type="button"
                              className="win95-row__open"
                              onClick={(e) => { e.stopPropagation(); selectPitchForReview(p); }}
                              title={`Open ${p.businessName || "pitch"}`}
                            >
                              {p.businessName || "No business name"}
                            </button>
                          </div>
                          <div className="win95-row__cell win95-row__cell--founder">{p.founderName || "—"}</div>
                          <div className="win95-row__cell win95-row__cell--chapter">{p.chapter || "—"}</div>
                          <div className="win95-row__cell win95-row__cell--data win95-row__cell--qtr">{p.quarter || "—"}</div>
                          <div className="win95-row__cell win95-row__cell--category">{p.category || "—"}</div>
                          <div className="win95-row__cell win95-row__cell--data win95-row__cell--comments" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <OSIcon32 kind="comment" size={15} />
                            {commentCount}
                          </div>
                          <div className="win95-row__cell win95-row__cell--status">
                            <button
                              type="button"
                              className="win95-chip"
                              style={{ background: chip.bg, color: chip.fg }}
                              onClick={(e) => { e.stopPropagation(); selectPitchForReview(p); }}
                              title={`Open ${p.businessName || "pitch"} to ${review ? "edit" : "submit"} a review`}
                            >
                              <OSIcon32 kind={chip.icon} size={13} />
                              {chip.label}
                            </button>
                          </div>
                          {summary && (
                            <div className="win95-row__summary">
                              <span className="win95-row__summary-text">{summary}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Action bar — rate the highlighted row without opening it.
                  On phones a tap opens the row directly, so the Open and
                  Prev/Next helpers (and the label) hide and the four rate
                  buttons share one row. */}
              <div className="win95-actionbar">
                <span className="win95-actionbar__label pr-actionbar-desktop">Rate selected</span>
                {RATE_ACTIONS.map((a) => (
                  <button
                    key={a.rating}
                    type="button"
                    className="win95-btn win95-btn--compact"
                    style={{ background: a.bg }}
                    disabled={!selectedRow || !onQuickRate}
                    onClick={() => onQuickRate && selectedRow && onQuickRate(selectedRow, a.rating)}
                    title={selectedRow ? `Rate ${selectedRow.businessName} as ${a.label}` : "Select a row first"}
                  >
                    <OSIcon32 kind={a.icon} size={13} />
                    {a.label}
                  </button>
                ))}
                <div className="win95-toolbar__sep pr-actionbar-desktop" aria-hidden="true" />
                <button
                  type="button"
                  className="win95-btn win95-btn--compact pr-actionbar-desktop"
                  disabled={!selectedRow}
                  onClick={() => selectedRow && selectPitchForReview(selectedRow)}
                >
                  Open
                </button>
                <div className="win95-toolbar__spacer pr-actionbar-desktop" />
                <button type="button" className="win95-btn win95-btn--compact pr-actionbar-desktop" onClick={() => moveSelection(-1)}>
                  ◀ Prev
                </button>
                <button type="button" className="win95-btn win95-btn--compact pr-actionbar-desktop" onClick={() => moveSelection(1)}>
                  Next ▶
                </button>
              </div>

              {/* Status bar */}
              <div className="win95-statusbar">
                <div className="win95-statusbar__pane win95-statusbar__pane--grow">
                  {lpPitches.length} pitches · {lpFilteredPitches.length} shown
                  {reviewQuarterFilter.length > 0 ? ` · filter: ${reviewQuarterFilter.join(", ")}` : ""}
                </div>
                <div className="win95-statusbar__pane win95-statusbar__pane--data">
                  {reviewedThisQuarter} reviewed this quarter
                </div>
                <div className="win95-statusbar__pane win95-statusbar__pane--data">Connected</div>
              </div>
            </div>
          /* --- Pitch Detail and Review Form View --- */
          ) : (
            <div className="win95-scroll" style={{ display: "flex", flexDirection: 'column', gap: "20px", padding: "20px", overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
              {/* Back Button */}
              <button
                type="button"
                className="pr-back"
                onClick={handleBackToList}
              >
                ← Back to list
              </button>

              {/* Two-column grid: pitch details + review form */}
              <div className="pr-grid">

                {/* Pitch Details Column */}
                <div className="pr-card">
                  <div className="pr-header">
                    <h3 className="pr-header__title">{selectedPitch.businessName || "N/A"}</h3>
                    {(selectedPitch.isContest || selectedPitch.isWinner) && (
                      <div className="pr-chips">
                        {selectedPitch.isContest && (
                          <span className="pr-chip pr-chip--contest">Contest submission</span>
                        )}
                        {selectedPitch.isWinner && (
                          <span className="pr-chip pr-chip--winner">
                            <StatusIcon type="trophy" size={14} />
                            Grant winner
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Founder + email + zip inline */}
                  <div className="pr-identity">
                    <span className="pr-identity__item">
                      <span className="pr-identity__label">Founder</span>
                      <span className="pr-identity__value">{selectedPitch.founderName || "N/A"}</span>
                    </span>
                    {selectedPitch.email && (
                      <span className="pr-identity__item">
                        <span className="pr-identity__label">Email</span>
                        <a href={`mailto:${selectedPitch.email}`}>{selectedPitch.email}</a>
                      </span>
                    )}
                    <span className="pr-identity__item">
                      <span className="pr-identity__label">Zip</span>
                      <span className="pr-identity__value">{selectedPitch.zipCode || "N/A"}</span>
                      {isLMIZip(selectedPitch.zipCode) && (
                        <span
                          className="pr-identity__lmi"
                          title={`ZIP median household income ≤ $${LMI_THRESHOLD.toLocaleString()} (ACS ${LMI_ACS_YEAR} 5-Year)`}
                        >
                          LMI
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Action buttons — left-aligned row */}
                  {(selectedVideoLink || selectedPitch.website) && (
                    <div className="pr-actions">
                      {selectedVideoLink && (
                        <button
                          type="button"
                          className="pr-btn"
                          onClick={() => window.open(selectedVideoLink, '_blank', 'noopener,noreferrer')}
                        >
                          ▶ Watch pitch video{selectedVideoHost ? ` · ${selectedVideoHost}` : ""}
                        </button>
                      )}
                      {selectedPitch.website && (
                        <button
                          type="button"
                          className="pr-btn pr-btn--ghost"
                          onClick={() => window.open(
                            selectedPitch.website.startsWith('http') ? selectedPitch.website : `//${selectedPitch.website}`,
                            '_blank',
                            'noopener,noreferrer'
                          )}
                        >
                          Visit website
                        </button>
                      )}
                    </div>
                  )}

                  {(selectedPitch.aiSummary || selectedPitch.category) && (
                    <div className="pr-ai">
                      <div className="pr-eyebrow">AI summary</div>
                      {selectedPitch.aiSummary && (
                        <div className="pr-ai__body">{selectedPitch.aiSummary}</div>
                      )}
                      {selectedPitch.category && (
                        <div className="pr-ai__category">Category: {selectedPitch.category}</div>
                      )}
                    </div>
                  )}

                  <div className="pr-section">
                    <div className="pr-field">
                      <span className="pr-field__label">Value prop</span>
                      <div className="pr-field__value">{selectedPitch.valueProp || "N/A"}</div>
                    </div>

                    <div className="pr-field">
                      <span className="pr-field__label">Problem</span>
                      <div className="pr-field__value">{selectedPitch.problem || "N/A"}</div>
                    </div>

                    <div className="pr-field">
                      <span className="pr-field__label">Solution</span>
                      <div className="pr-field__value">{selectedPitch.solution || "N/A"}</div>
                    </div>

                    <div className="pr-field">
                      <span className="pr-field__label">Business model</span>
                      <div className="pr-field__value">{selectedPitch.businessModel || "N/A"}</div>
                    </div>

                    <div className="pr-field pr-field--inline">
                      <span className="pr-field__label">Paying customers</span>
                      <span className={selectedPitch.hasPayingCustomers ? "pr-yes" : "pr-no"}>
                        {selectedPitch.hasPayingCustomers ? 'Yes' : 'No'}
                      </span>
                    </div>

                    <div className="pr-field">
                      <span className="pr-field__label">Grant use</span>
                      <div className="pr-field__value">{selectedPitch.grantUsePlan || "N/A"}</div>
                    </div>
                  </div>

                  <div className="pr-section">
                    <div className="pr-field">
                      <span className="pr-field__label">Self identification tags</span>
                      <div className="pr-field__value">{selectedPitch.selfIdentification?.join(", ") || "N/A"}</div>
                    </div>

                    <div className="pr-field pr-field--inline">
                      <span className="pr-field__label">Heard about</span>
                      <span className="pr-field__value">{selectedPitch.heardAbout || "N/A"}</span>
                    </div>

                    <div className="pr-field pr-field--inline">
                      <span className="pr-field__label">Terms &amp; privacy agreed</span>
                      <span className={selectedPitch.consentToShare ? "pr-yes" : "pr-no"}>
                        {selectedPitch.consentToShare ? 'Yes' : 'No'}
                      </span>
                    </div>

                    <div className="pr-field pr-field--inline">
                      <span className="pr-field__label">In-person meetup agreed</span>
                      <span className={selectedPitch.consentToMeetup ? "pr-yes" : "pr-no"}>
                        {selectedPitch.consentToMeetup ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Review Form Column */}
                <div className="pr-card">
                  <h4 className="pr-form__title">
                    {reviews[selectedPitch.id] ? 'Edit your review' : 'Submit your review'}
                  </h4>
                  <div className="pr-form__subtitle">
                    {reviews[selectedPitch.id]
                      ? 'Update your recommendation and notes.'
                      : 'Rate this pitch and share your thoughts with the team.'}
                  </div>

                  <form onSubmit={handleReviewSubmit}>
                    {/* LP Recommendation - Required */}
                    <div className="pr-form__group pr-form__group--required">
                      <label className="pr-form__label" htmlFor="pr-overall-rating">
                        Overall LP recommendation
                        <span className="pr-form__required">(Required)</span>
                      </label>
                      <select
                        id="pr-overall-rating"
                        className="pr-select"
                        name="overallLpRating"
                        value={reviewFormData.overallLpRating || ""}
                        onChange={handleReviewFormChange}
                        required
                      >
                        <option value="" disabled>Select an overall rating…</option>
                        <option value="Favorite">Favorite (strongly recommend)</option>
                        <option value="Consideration">Consideration (potential)</option>
                        <option value="Pass">Pass (not a fit / concerns)</option>
                        <option value="Ineligible">Ineligible (doesn't meet criteria)</option>
                      </select>
                    </div>

                    {/* Optional Rating Selects */}
                    <div className="pr-form__group-head">Additional ratings · optional</div>

                    <div className="pr-form__group">
                      <label className="pr-form__label" htmlFor="pr-video-rating">
                        Pitch video clarity &amp; persuasiveness
                        <span className="pr-form__optional">Optional</span>
                      </label>
                      <select
                        id="pr-video-rating"
                        className="pr-select"
                        name="pitchVideoRating"
                        value={reviewFormData.pitchVideoRating || ""}
                        onChange={handleReviewFormChange}
                      >
                        <option value="">Rate video…</option>
                        <option value="Strong">Strong</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div className="pr-form__group">
                      <label className="pr-form__label" htmlFor="pr-model-rating">
                        Business model viability
                        <span className="pr-form__optional">Optional</span>
                      </label>
                      <select
                        id="pr-model-rating"
                        className="pr-select"
                        name="businessModelRating"
                        value={reviewFormData.businessModelRating || ""}
                        onChange={handleReviewFormChange}
                      >
                        <option value="">Rate model…</option>
                        <option value="Strong">Strong</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div className="pr-form__group">
                      <label className="pr-form__label" htmlFor="pr-fit-rating">
                        Product market fit evidence
                        <span className="pr-form__optional">Optional</span>
                      </label>
                      <select
                        id="pr-fit-rating"
                        className="pr-select"
                        name="productMarketFitRating"
                        value={reviewFormData.productMarketFitRating || ""}
                        onChange={handleReviewFormChange}
                      >
                        <option value="">Rate fit…</option>
                        <option value="Strong">Strong</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    {/* Team Notes from other LPs */}
                    {selectedPitch && (() => {
                      const otherComments = (pitchComments[selectedPitch.id] || [])
                        .filter(c => c.reviewerId !== user.uid);
                      if (otherComments.length === 0) return null;
                      return (
                        <div className="pr-form__group">
                          <div className="pr-form__group-head" style={{ marginTop: 0 }}>
                            Notes from the team · {otherComments.length}
                          </div>
                          <div className="pr-team-notes">
                            {otherComments.map((c, i) => (
                              <div key={i} className="pr-team-note">
                                <div className="pr-team-note__who">
                                  {isSuperAdmin ? c.reviewerName : `LP ${i + 1}`}
                                </div>
                                <div className="pr-team-note__text">{c.comment}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Comments Textarea */}
                    <div className="pr-form__group">
                      <label className="pr-form__label" htmlFor="pr-comments">
                        Notes for the team
                        <span className="pr-form__optional">Optional</span>
                      </label>
                      <textarea
                        id="pr-comments"
                        className="pr-textarea"
                        name="comments"
                        rows="4"
                        value={reviewFormData.comments || ""}
                        onChange={handleReviewFormChange}
                        placeholder="Share your thoughts, strengths, concerns, or questions with the team…"
                      />
                    </div>

                    {/* Submit + Next Unreviewed Buttons (side by side) */}
                    {(() => {
                      const nextPitch = getNextUnreviewedPitch();
                      // While a submit is in flight the label must come from the
                      // captured edit flag — setReviews lands mid-flight and would
                      // otherwise flip a first submit's label to "Updated".
                      const submitLabel = reviewSubmitPhase === 'saving'
                        ? (reviewSubmitWasEdit ? 'Updating…' : 'Submitting…')
                        : reviewSubmitPhase === 'saved'
                          ? (reviewSubmitWasEdit ? '✓ Review updated' : '✓ Review submitted')
                          : (reviews[selectedPitch.id] ? 'Update review' : 'Submit review');
                      return (
                        <div className="pr-submit-row">
                          <button
                            type="submit"
                            className={`pr-btn pr-btn--primary${
                              reviewSubmitPhase === 'saving' ? ' pr-btn--saving' : ''
                            }${reviewSubmitPhase === 'saved' ? ' pr-btn--saved' : ''}`}
                            disabled={reviewSubmitPhase !== 'idle'}
                            aria-live="polite"
                          >
                            {submitLabel}
                          </button>
                          {nextPitch && (
                            <button
                              type="button"
                              className="pr-btn pr-btn--ghost"
                              onClick={() => setSelectedPitch(nextPitch)}
                              disabled={reviewSubmitPhase === 'saving'}
                              title={`Next unreviewed: ${nextPitch.businessName}`}
                            >
                              Next unreviewed →
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
  );
}
